import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { History, TrendingUp, TrendingDown, UserMinus, UserCheck, ArrowLeftRight } from "lucide-react";
import { Link } from "react-router-dom";

const COMPANY_LABELS = {
  misas: { label: "Misaş", cls: "company-misas" },
  mis_holding: { label: "Mis Holding", cls: "company-mh" },
  koop: { label: "Mis Koop.", cls: "company-koop" },
  "*": { label: "Genel", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

const ACTION_META = {
  update: { icon: TrendingUp, text: "Hisse güncellendi" },
  depart: { icon: UserMinus, text: "Ortaklıktan çıkarıldı" },
  restore: { icon: UserCheck, text: "Ortak geri alındı" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function ShareHistory({ memberId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/members/${memberId}/history`)
      .then(r => setRows(r.data))
      .finally(() => setLoading(false));
  }, [memberId]);

  return (
    <div className="crisp-card p-6" data-testid="share-history">
      <div className="flex items-center gap-2 mb-4">
        <History size={16} className="text-slate-500" />
        <h3 className="text-sm font-bold text-slate-900">Değişiklik Geçmişi</h3>
      </div>
      {loading && <div className="text-sm text-slate-500">Yükleniyor...</div>}
      {!loading && rows.length === 0 && (
        <div className="text-sm text-slate-500 py-4">Bu ortak için henüz kaydedilmiş bir değişiklik yok.</div>
      )}
      {!loading && rows.length > 0 && (
        <ol className="relative border-l-2 border-slate-100 ml-2 space-y-4">
          {rows.map(h => {
            const co = COMPANY_LABELS[h.company] || COMPANY_LABELS["*"];
            const meta = ACTION_META[h.action] || ACTION_META.update;
            const Icon = h.action === "update" && h.delta < 0 ? TrendingDown : meta.icon;
            const positive = (h.delta || 0) > 0;
            return (
              <li key={h.id} className="ml-5 relative stagger-row" data-testid={`history-${h.id}`}>
                <span className="absolute -left-[27px] top-1 h-4 w-4 rounded-full bg-white border-2 border-slate-300" />
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${co.cls}`}>{co.label}</span>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-700 font-semibold">
                      <Icon size={12} className={h.action === "update" ? (positive ? "text-emerald-600" : "text-red-600") : "text-slate-500"} />
                      {meta.text}
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-500 font-mono-nums">{fmtDate(h.changed_at)}</span>
                </div>
                {h.action === "update" && (
                  <div className="mt-1.5 text-sm font-mono-nums">
                    <span className="text-slate-500">{h.old_value}</span>
                    <span className="mx-2 text-slate-400">→</span>
                    <span className="font-bold text-slate-900">{h.new_value}</span>
                    <span className={`ml-2 text-xs font-semibold ${positive ? "text-emerald-600" : "text-red-600"}`}>
                      ({positive ? "+" : ""}{h.delta})
                    </span>
                  </div>
                )}
                {h.paired_member_id && (
                  <Link to={`/ortak/${h.paired_member_id}`}
                    className="inline-flex items-center gap-1.5 mt-1.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2.5 py-0.5 text-[11px] font-semibold hover:bg-amber-100"
                    data-testid={`devir-link-${h.id}`}
                    onClick={e => e.stopPropagation()}>
                    <ArrowLeftRight size={11}/>
                    {positive ? `${h.paired_member_ad_soyad}'den gelen` : `${h.paired_member_ad_soyad}'e giden`} devir
                  </Link>
                )}
                {h.note && (
                  <div className="mt-1 text-xs text-slate-500 whitespace-pre-wrap">{h.note}</div>
                )}
                <div className="mt-1 text-[11px] text-slate-400">
                  Değiştiren: <span className="text-slate-600 font-semibold">{h.changed_by_name || h.changed_by || "—"}</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
